import {
  load as loadDatabook,
  type DatabookDeviceData,
  type DatabookStore,
  type ParameterData,
} from '@ebiz/aditools-databook';
import Service from '@ember/service';
import { service } from '@ember/service';
import type CdpService from '../services/cdp';
import type ModelService from '../services/model';
import type { OpAmpSpec } from '../services/model';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import * as AditoolsSpiceParts from '@ebiz/aditools-spice/data/opamps.json';

import type LoggingService from './logger';

enum DataType {
  t_pulldown = 'Pulldown',
  t_float = 'Floating Point',
  t_integer = 'Integer',
  t_string = 'String',
  t_none = 'N/A',
}

enum CardinalityType {
  c_single = 'single',
  c_multi = 'multi',
  c_none = 'N/A',
}

interface PstParameterDescriptor<ParmName extends string> {
  id: string;
  name: ParmName;
  data: DataType;
  value: CardinalityType;
  optional: ((part: DatabookDeviceData) => boolean) | boolean;
  default?: (part: Record<ParmName, string>) => string | undefined;
}
type PstParameterSet<ParmName extends string> = Record<string, PstParameterDescriptor<ParmName>>;

type SpicePin = {
  function: string;
};

export type SpicePartData = {
  offsetX: string;
  offsetY: string;
  spicePartNumber: string;
  symbolPath: string;
  pins: SpicePin[];
};

type SpiceParts = Record<string, SpicePartData>;

type LocalPart = {
  partId: string;
  [key: string]: string | { value: string[] };
};

type PolesZeroesParts = Record<string, PolesZeroesData>;

export interface PolesZeroesData {
  gbw: number;
  domPole: number;
  p: number[];
  z: number[];
  rhpz: number;
  [key: string]: number | number[];
}

type CfbParts = Record<string, CfbData>;

export type CfbData = {
  rIn: number;
  minRf: number;
  zbw: number;
};
export type OpAmpPSTParameterData =
  | string
  | string[]
  | CfbData
  | PolesZeroesData
  | SpicePartData
  | number
  | boolean
  | ParameterData;

export type OpAmpPSTParameterName =
  | 'partNumber'
  | 'gbw'
  | 'slewRate'
  | 'rOut'
  | 'supplyCurrent'
  | 'iNoise'
  | 'vNoise'
  | 'outHeadroomPlus'
  | 'outHeadroomMinus'
  | 'inHeadroomPlus'
  | 'inHeadroomMinus'
  | 'feedbackMethod'
  | 'architecture'
  | 'minSupplyVoltage'
  | 'maxSupplyVoltage'
  | 'numberChannels'
  | 'packageCode'
  | 'minStableGain'
  | 'iBias'
  | 'vos'
  | 'vintage'
  | 'statusText'
  | 'deviceStatus'
  | 'price'
  | 'tempRange'
  | 'cCm'
  | 'cDiff'
  | 'generic'
  | 'spice'
  | 'cfb'
  | 'spiceModels'
  | 'polesZeroes';

export interface DataServiceLoadResult {
  opAmp: Record<OpAmpPSTParameterName, OpAmpPSTParameterData>[];
  databook: DatabookStore;
}

export const PST_PARAMETERS: PstParameterSet<OpAmpPSTParameterName> = {
  '0': {
    id: 'partNumber',
    name: 'partNumber',
    data: DataType.t_none,
    value: CardinalityType.c_none,
    optional: true,
  },
  '4502': {
    id: 'gbpHz',
    name: 'gbw',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: (part) => {
      return (part['feedbackType'] as ParameterData)?.value?.[0] === 'Current';
    },
  },
  '300': {
    id: 'slewRateVus',
    name: 'slewRate',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4401': {
    id: 'outputImpedanceOhms',
    name: 'rOut',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: true,
  },
  '4108': {
    id: 'supplyCurrentTyp',
    name: 'supplyCurrent',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4124': {
    id: 'currentNoiseDensity',
    name: 'iNoise',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
    default: (part) => {
      if (
        'iBias' in part &&
        !isNaN(Number(part.iBias)) &&
        parseFloat(part.iBias) < 80e-12 &&
        'architecture' in part &&
        part.architecture.length > 0 &&
        -1 === part.architecture.indexOf('Zero Drift')
      ) {
        return '1e-12';
      } else {
        return;
      }
    },
  },
  '4101': {
    id: 'vNoiseDensity',
    name: 'vNoise',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4099': {
    id: 'outVMinusHeadroomV',
    name: 'outHeadroomMinus',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4100': {
    id: 'outVPlusHeadroomV',
    name: 'outHeadroomPlus',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4084': {
    id: 'inVPlusHeadroomV',
    name: 'inHeadroomPlus',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4085': {
    id: 'inVMinusHeadroomV',
    name: 'inHeadroomMinus',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '5034': {
    id: 'feedbackType',
    name: 'feedbackMethod',
    data: DataType.t_pulldown,
    value: CardinalityType.c_single,
    optional: true,
  },
  '5056': {
    id: 'inputArchitecture',
    name: 'architecture',
    data: DataType.t_pulldown,
    value: CardinalityType.c_multi,
    optional: true,
  },
  '2839': {
    id: 'vsSpanMinV',
    name: 'minSupplyVoltage',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '2840': {
    id: 'vsSpanMaxV',
    name: 'maxSupplyVoltage',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '2687': {
    id: 'numberOfChannels',
    name: 'numberChannels',
    data: DataType.t_integer,
    value: CardinalityType.c_single,
    optional: false,
  },
  s4: {
    id: 'packageCode',
    name: 'packageCode',
    data: DataType.t_pulldown,
    value: CardinalityType.c_multi,
    optional: false,
  },
  '4127': {
    id: 'minStableGainVV',
    name: 'minStableGain',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4500': {
    id: 'ibiasMaxA',
    name: 'iBias',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4501': {
    id: 'vosMaxV',
    name: 'vos',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4455': {
    id: 'ccmF',
    name: 'cCm',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  '4117': {
    id: 'cDiffF',
    name: 'cDiff',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: false,
  },
  s1: {
    id: 'vintage',
    name: 'vintage',
    data: DataType.t_integer,
    value: CardinalityType.c_single,
    optional: true,
  },
  s7: {
    id: 'statusText',
    name: 'statusText',
    data: DataType.t_string,
    value: CardinalityType.c_single,
    optional: true,
  },
  s11: {
    id: 'status',
    name: 'deviceStatus',
    data: DataType.t_integer,
    value: CardinalityType.c_single,
    optional: true,
  },
  s3: {
    id: 'price',
    name: 'price',
    data: DataType.t_float,
    value: CardinalityType.c_single,
    optional: true,
  },
  s14: {
    id: 'temperature',
    name: 'tempRange',
    data: DataType.t_string,
    value: CardinalityType.c_single,
    optional: true,
  },
};

export default class DataService extends Service {
  @service('aditools-site') declare siteconfig: SiteService;
  @service('model') declare modelService: ModelService;
  @service declare cdp: CdpService;
  @service declare logger: LoggingService;
  opAmps?: Promise<OpAmpSpec[] | null>;

  async load(config?: Promise<PhotodiodeConfigJson | null>): Promise<DataServiceLoadResult> {
    const fetchUrl =
      window.location.hostname === 'localhost'
        ? `${window.location.protocol}//${window.location.host}/`
        : `${this.siteconfig['SiteUrl']}/`;
    const logger = this.logger;
    const index: DatabookDeviceData[] = [];
    const resolvedConfig = config ? await config : null;
    if (resolvedConfig && resolvedConfig.cdpsource.useLocalPartsData) {
      collectConfigData(index, this.siteconfig['AdiLocale'], resolvedConfig);
    }

    const databook = await loadDatabook(
      fetchUrl,
      [
        {
          deviceType: 'opAmp',
          supplementDevices: index || [],
          // deviceFilterFn: (device) => {
          //     return PART_IS_USABLE(device, 'OpAmp', hiddenDevices?.opAmp || []);
          // },
          // supplementColumnDataFn
        },
      ],
      {},
    );
    const opamps = interpretDatabookOpAmps(databook.details['opAmp']!.data, PST_PARAMETERS, logger);
    return {
      opAmp: opamps,
      databook: databook,
    };
  }
}

function collectConfigData(index: DatabookDeviceData[], adiLocale: string, config: PhotodiodeConfigJson): void {
  if (!config) return;
  if (config.cdpsource?.useLocalPartsData && config.cdpsource?.localParts) {
    ((config.cdpsource.localParts as unknown as Record<string, LocalPart[]>)[adiLocale] || []).forEach(
      (part: LocalPart) => {
        const { partId, ...partToAdd } = part;
        addPart(partId, '', partToAdd, index);
      },
    );
  }
  const polesZeroes: PolesZeroesParts = config.polesZeroes as PolesZeroesParts;
  if (polesZeroes) {
    Object.keys(polesZeroes).forEach((id) => {
      addPart(id, 'polesZeroes', polesZeroes[id]!, index);
    });
  }
  const cfdDetails: CfbParts = config['cfbParts'] as CfbParts;
  if (cfdDetails) {
    Object.keys(cfdDetails).forEach((id) => {
      addPart(id, 'cfb', cfdDetails[id]!, index);
    });
  }
  // Spice Parts are removed in configuration and we will now use
  // the spice parts in aditools-spice
  const spiceParts: SpiceParts = AditoolsSpiceParts;
  if (spiceParts) {
    Object.keys(spiceParts).forEach((id) => {
      addPart(
        id,
        '',
        {
          spiceModels: spiceParts[id]!,
          spice: spiceParts[id] !== undefined ? 'Yes' : 'No',
        },
        index,
      );
    });
  }
}

function addPart(id: string, type: string, value: Record<string, unknown>, partList: DatabookDeviceData[]): void {
  const valueProp = type !== '' ? { [type]: value } : value;
  const idx = partList.findIndex((item) => item['partNumber'].value[0] === id);
  if (idx !== -1) {
    partList[idx] = Object.assign({}, partList[idx], valueProp);
  } else {
    partList.push(
      Object.assign(
        {},
        { partNumber: { value: [id], displayValue: id, generic: id } },
        valueProp,
      ) as DatabookDeviceData,
    );
  }
}

function interpretParameter(
  parameter: ParameterData,
  datatype: DataType,
  datavalue: CardinalityType,
): [string | null, string | null] {
  if (
    parameter === undefined ||
    parameter === null ||
    typeof parameter !== 'object' ||
    !('value' in parameter) ||
    parameter.value === null ||
    typeof parameter.value.length === 'undefined'
  ) {
    return [null, 'INVALID'];
  }
  if (parameter.value.length === 0 || (parameter.value.length === 1 && parameter.value[0] === '')) {
    return [null, 'EMPTY'];
  }
  if (datatype === DataType.t_pulldown && datavalue === CardinalityType.c_multi) {
    return [parameter.value.join('; '), null];
  } else {
    return [parameter.value[0] ?? null, null];
  }
}

const LOG_SKIPS = false;
const LOG_SYNTH = false;

const convertPart = function (
  mergedPart: DatabookDeviceData,
  parameters: PstParameterSet<OpAmpPSTParameterName>,
  logging: LoggingService,
): Record<OpAmpPSTParameterName, OpAmpPSTParameterData> | null {
  const part: Record<OpAmpPSTParameterName, OpAmpPSTParameterData> = {
    partNumber: mergedPart['partNumber']?.value?.[0],
  } as Record<OpAmpPSTParameterName, OpAmpPSTParameterData>;
  const missingParms: string[] = [];
  const emptyParms: string[] = [];
  const invalidParms: string[] = [];
  // Fill in all the part's parameters based on the table and keep track of we couldn't use
  Object.values(parameters).forEach((parminfo) => {
    const isOptional = typeof parminfo.optional === 'function' ? parminfo.optional(mergedPart) : parminfo.optional;
    if (parminfo.id !== 'partNumber') {
      // Skip the name - we've already got it
      if (parminfo.id in mergedPart) {
        const [value, error] = interpretParameter(
          mergedPart[parminfo.id] as ParameterData,
          parminfo.data,
          parminfo.value,
        );
        if (error) {
          if (error === 'INVALID') {
            invalidParms.push(`${parminfo.name}(${parminfo.id})`);
          } else if (error === 'EMPTY') {
            if (!isOptional && !('default' in parminfo)) {
              emptyParms.push(`${parminfo.name}(${parminfo.id})`);
            }
          }
        } else {
          part[parminfo.name] = value!;
        }
      } else {
        if (!isOptional) {
          missingParms.push(`${parminfo.name}(${parminfo.id})`);
        }
      }
    } else {
      part['generic'] = mergedPart[parminfo.id].generic
        ? mergedPart[parminfo.id].generic
        : mergedPart[parminfo.id].value;
    }
  });

  // A second pass to fill in the defaults for the part's parameters based on other parameters
  Object.values(parameters).forEach((parminfo) => {
    if ('default' in parminfo) {
      if (!(parminfo.name in part)) {
        const value = parminfo.default!(part as Record<OpAmpPSTParameterName, string>);
        const isOptional =
          typeof parminfo.optional === 'function'
            ? parminfo.optional(part as Record<OpAmpPSTParameterName, string>)
            : parminfo.optional;
        if (value === null && !isOptional) {
          emptyParms.push(`${parminfo.name}(${parminfo.id})`);
        } else {
          part[parminfo.name] = value!;
        }
      }
    }
  });

  // Record why we're not delivering a part
  if (missingParms.length > 0 || emptyParms.length > 0 || invalidParms.length > 0) {
    if (LOG_SKIPS) {
      const causes = [];
      if (invalidParms.length > 0) {
        causes.push(`invalid parms ${invalidParms.join(', ')}`);
      }
      if (emptyParms.length > 0) {
        causes.push(`empty parms ${emptyParms.join(', ')}`);
      }
      if (missingParms.length > 0) {
        causes.push(`missing parms ${missingParms.join(', ')}`);
      }
      logging.info(`Skipping ${mergedPart['partNumber']?.value?.[0]} due to ${causes.join('; ')} `);
    }
    return null;
  }

  if ('spiceModels' in mergedPart) {
    part['spiceModels'] = mergedPart['spiceModels'];
  }
  if ('polesZeroes' in mergedPart) {
    part['polesZeroes'] = mergedPart['polesZeroes'];
  } else if (typeof mergedPart['id'] === 'string' && mergedPart['id'].substring(0, 2) === 'LT') {
    const GBW = part['gbw'] as number;
    if (LOG_SYNTH) {
      logging.info('Synthesizing polesZeroes for ' + mergedPart['partNumber']?.value?.[0]);
    }
    part['polesZeroes'] = {
      gbw: GBW,
      domPole: 0.15 * GBW,
      p: [2.5 * GBW],
      z: [0.5 * GBW],
      rhpz: 1e14,
    };
  }
  if ('spice' in mergedPart) {
    part['spice'] = mergedPart['spice'];
  }
  if ('cfb' in mergedPart) {
    part['cfb'] = mergedPart['cfb'];
  }
  return part;
};

const interpretDatabookOpAmps = function (
  databookOpAmp: DatabookDeviceData[],
  parameters: PstParameterSet<OpAmpPSTParameterName>,
  logging: LoggingService,
): Record<OpAmpPSTParameterName, OpAmpPSTParameterData>[] {
  try {
    const opamps: Record<OpAmpPSTParameterName, OpAmpPSTParameterData>[] = [];
    databookOpAmp.map((item) => {
      const convertedPart = convertPart(item, parameters, logging);
      if (convertedPart) {
        opamps.push(convertedPart);
      }
    });
    return opamps;
  } catch (e) {
    logging.warn('Failure occurred interpreting opamp data:', (e as Error).message);
    return [];
  }
};
